/**
 * Single place that loads Razorpay's checkout SDK.
 *
 * The SDK costs ~350ms of main-thread CPU, so it must NEVER be injected on
 * mount. Load it either:
 *   - `warmRazorpay()` on purchase *intent* (pointerenter / focus / touchstart
 *     on a pay button), which is free because it happens after first paint, or
 *   - `loadRazorpay()` at pay time, which awaits the same single promise.
 */

const SRC = "https://checkout.razorpay.com/v1/checkout.js";
const TIMEOUT_MS = 10_000;

let pending: Promise<boolean> | null = null;

function hasSdk(): boolean {
  return typeof window !== "undefined" && !!(window as unknown as { Razorpay?: unknown }).Razorpay;
}

/** Sends a readable record of a failed SDK load to the error log. */
function reportSdkFailure(reason: "load_error" | "timeout", startedAt: number): void {
  const elapsedMs = Math.round(performance.now() - startedAt);
  void import("@/lib/analytics")
    .then(({ captureError }) =>
      captureError(new Error(`Razorpay checkout script ${reason === "timeout" ? "timed out" : "failed to load"}`), {
        source: "razorpay_sdk",
        reason,
        elapsedMs,
        online: typeof navigator !== "undefined" ? navigator.onLine : null,
      }),
    )
    .catch(() => {});
}

/** Warm the SDK once the browser is idle, so it never competes with first paint. */
export function warmRazorpayWhenIdle(): void {
  if (typeof window === "undefined") return;
  const ric = (window as unknown as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number })
    .requestIdleCallback;
  if (ric) ric(() => warmRazorpay(), { timeout: 1500 });
  else setTimeout(warmRazorpay, 300);
}

/** Resolves true once window.Razorpay exists. Idempotent across call sites. */
export function loadRazorpay(): Promise<boolean> {
  if (typeof window === "undefined") return Promise.resolve(false);
  if (hasSdk()) return Promise.resolve(true);
  if (pending) return pending;

  pending = new Promise<boolean>((resolve) => {
    const settle = (ok: boolean) => resolve(ok || hasSdk());
    const existing = document.querySelector<HTMLScriptElement>(`script[src*="checkout.razorpay.com"]`);
    const script = existing ?? document.createElement("script");
    if (!existing) {
      // Razorpay's CDN sends `Access-Control-Allow-Origin: *`, so loading it
      // CORS-mode makes errors thrown inside the SDK readable in our error log
      // instead of the browser's opaque "Script error.".
      script.crossOrigin = "anonymous";
      script.src = SRC;
      script.async = true;
      (script as HTMLScriptElement & { fetchPriority?: string }).fetchPriority = "low";
    }
    const startedAt = performance.now();
    script.addEventListener("load", () => settle(true));
    script.addEventListener("error", () => {
      pending = null;
      reportSdkFailure("load_error", startedAt);
      settle(false);
    });
    setTimeout(() => {
      if (!hasSdk()) reportSdkFailure("timeout", startedAt);
      settle(false);
    }, TIMEOUT_MS);
    if (!existing) document.body.appendChild(script);
  });

  return pending;
}

/**
 * Drops the SDK so the next `loadRazorpay()` fetches a fresh copy. Our
 * teardown deletes checkout's frame; the old SDK keeps a reference to that
 * dead frame and a second open() fails with Razorpay's "This browser is not
 * supported" alert.
 */
export function resetRazorpaySdk(): void {
  if (typeof window === "undefined") return;
  document.querySelectorAll(`script[src*="checkout.razorpay.com"]`).forEach((n) => n.remove());
  try {
    delete (window as unknown as { Razorpay?: unknown }).Razorpay;
  } catch {
    (window as unknown as { Razorpay?: unknown }).Razorpay = undefined;
  }
  pending = null;
}

/** Fire-and-forget warm on purchase intent. Never throws, never blocks. */
export function warmRazorpay(): void {
  if (typeof window === "undefined" || hasSdk() || pending) return;
  void loadRazorpay();
}

/** Props to spread on a pay button/plan card so the SDK warms on intent. */
export const razorpayWarmHandlers = {
  onPointerEnter: warmRazorpay,
  onFocus: warmRazorpay,
  onTouchStart: warmRazorpay,
} as const;
